import { Injectable } from '@angular/core';
import { StoreService } from './store.service';
import type { ClaveColorPrimario } from '../shared/config/colores-primarios.config';
import { COLORES_PRIMARIOS, COLOR_PRIMARIO_DEFAULT } from '../shared/config/colores-primarios.config';

/** Valida clave de color primario; claves obsoletas (p. ej. violeta, naranja) caen al default. */
function normalizarColorPrimario(valor: unknown): ClaveColorPrimario {
  if (typeof valor === 'string' && valor in COLORES_PRIMARIOS) {
    return valor as ClaveColorPrimario;
  }
  return COLOR_PRIMARIO_DEFAULT;
}

/** Override de conectividad para pruebas desde Configuración. */
export type ModoSimulacionOffline = 'automatico' | 'si' | 'no';

/**
 * Interfaz del estado de configuración
 */
export interface ConfigState {
  tema: 'claro' | 'oscuro' | 'auto';
  notificaciones: boolean;
  idioma: 'es' | 'en';
  /** Color primario en tema claro. Solo aplica cuando tema efectivo es claro. */
  colorPrimario: ClaveColorPrimario;
  /** Simula offline/online; `automatico` usa la red real del dispositivo. */
  simularOffline: ModoSimulacionOffline;
}

/**
 * Estado inicial de configuración
 */
const initialState: ConfigState = {
  /** Por defecto tema claro en el primer arranque; quien ya tenga preferencia guardada en localStorage no se ve afectado. */
  tema: 'claro',
  notificaciones: true,
  idioma: 'es',
  colorPrimario: COLOR_PRIMARIO_DEFAULT,
  simularOffline: 'automatico',
};

/**
 * Store de configuración usando el patrón similar a Zustand
 */
@Injectable({
  providedIn: 'root'
})
export class ConfigStore extends StoreService<ConfigState> {
  protected getInitialState(): ConfigState {
    const key = this.getStorageKey();
    if (!key) return initialState;
    try {
      const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null;
      if (raw) {
        const parsed = JSON.parse(raw) as Partial<ConfigState>;
        if (parsed && typeof parsed.tema === 'string' && ['claro', 'oscuro', 'auto'].includes(parsed.tema)) {
          return {
            ...initialState,
            ...parsed,
            colorPrimario: normalizarColorPrimario(parsed.colorPrimario),
          };
        }
      }
    } catch {
      // Ignorar errores; usar estado por defecto
    }
    return initialState;
  }

  protected getStorageKey(): string | undefined {
    return 'indap_config_state';
  }

  /**
   * Selectores
   */
  get tema$() {
    return this.select(state => state.tema);
  }

  get notificaciones$() {
    return this.select(state => state.notificaciones);
  }

  get idioma$() {
    return this.select(state => state.idioma);
  }

  get colorPrimario$() {
    return this.select(state => state.colorPrimario);
  }

  get simularOffline$() {
    return this.select(state => state.simularOffline);
  }

  /**
   * Acciones
   */
  cambiarTema(tema: 'claro' | 'oscuro' | 'auto'): void {
    this.setState({ tema });
  }

  cambiarColorPrimario(colorPrimario: ClaveColorPrimario): void {
    this.setState({ colorPrimario });
  }

  cambiarNotificaciones(notificaciones: boolean): void {
    this.setState({ notificaciones });
  }

  cambiarIdioma(idioma: 'es' | 'en'): void {
    this.setState({ idioma });
  }

  cambiarSimularOffline(simularOffline: ModoSimulacionOffline): void {
    this.setState({ simularOffline });
  }

  obtenerTema(): 'claro' | 'oscuro' | 'auto' {
    return this.getState().tema;
  }

  obtenerNotificaciones(): boolean {
    return this.getState().notificaciones;
  }

  obtenerIdioma(): 'es' | 'en' {
    return this.getState().idioma;
  }

  obtenerColorPrimario(): ClaveColorPrimario {
    const normalizado = normalizarColorPrimario(this.getState().colorPrimario);
    if (normalizado !== this.getState().colorPrimario) {
      this.setState({ colorPrimario: normalizado });
    }
    return normalizado;
  }

  obtenerSimularOffline(): ModoSimulacionOffline {
    const valor = this.getState().simularOffline;
    if (valor === 'si' || valor === 'no' || valor === 'automatico') {
      return valor;
    }
    return 'automatico';
  }
}






